<template>
  <div v-if="!taking && !editing" class="lesson-widget-summary">
    <p>{{ summary }}</p>
  </div>

  <div class="widget-card--wrap" v-else>
    <editor v-if="!taking" :item="item" :lessonId="lesson.id" />
    <v-card
      v-if="formId && !completed"
      class="widget-card jotform-widget"
      :class="{ 'widget-preview': !taking }"
    >
      <iframe
        :id="iframeId"
        :src="iframeSrc"
        title="Form"
        allowtransparency="true"
        allowfullscreen="true"
        allow="geolocation; microphone; camera"
        frameborder="0"
        style="min-width: 100%; height: 539px; border: none"
        scrolling="no"
      >
      </iframe>
    </v-card>

    <h5 v-if="completed && successText" class="text-center mt-2">
      {{ successText }}
    </h5>
  </div>
</template>

<script>
import GenericWidget from '../genericWidget/GenericWidget';
import EngagementWidget from '../engagementWidget/EngagementWidget';

export default {
  name: 'jotformWidget',
  mixins: [GenericWidget, EngagementWidget],
  watch: {
    active: {
      immediate: true,
      handler(isActive, wasActive) {
        if (isActive) {
          this.initJotForm();
        }

        if (wasActive && !isActive) {
          this.toggleEventListeners();
        }
      },
    },
  },
  computed: {
    formId() {
      return this.val.form_id;
    },
    iframeSrc() {
      let src = `https://form.jotform.com/${this.formId}`;
      let fields = this.prefilledFields;

      if (fields) {
        fields = Object.entries(fields)
          .map(([key, value]) => {
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
          })
          .join('&');
        src = `${src}?${fields}`;
      }

      return src;
    },
    iframeId() {
      return `JotFormIFrame-${this.formId}`;
    },
    summary() {
      if (!this.formId) return '';
      return `Form ID: ${this.formId}`;
    },

    /**
     * Reads server-compiled prefilled field values from `item.valuesCompiled`
     * (populated by `Engagement\Jotform::getCompiledValues()` on the backend),
     * with a fallback to the raw `val.prefilled_fields` for deploy windows
     * where the backend has not yet shipped the compile support. All token
     * substitution happens server-side via `Contact::compileContent()`, which
     * has access to every contact field including custom dropdown fields like
     * team. This computed just reshapes the KeyValues array into a flat
     * `{key: value}` object for the iframe query string.
     */
    prefilledFields() {
      const config = this.valCompiled('prefilled_fields');
      if (!Array.isArray(config)) return false;

      const fields = {};
      const clean = (v) => (v || '').trim();

      config.forEach((pair) => {
        const key = clean(pair.key);
        const value = clean(pair.value);
        if (!key || !value) return;
        fields[key] = value;
      });

      return fields;
    },

    successText() {
      return this.valCompiled('success_text');
    },
  },
  methods: {
    async initJotForm() {
      await this.$nextTick();

      if (!this.formId || this.completed) return;

      var ifr = document.getElementById(`JotFormIFrame-${this.formId}`);
      if (ifr) {
        var src = ifr.src;
        var iframeParams = [];
        if (window.location.href && window.location.href.indexOf('?') > -1) {
          iframeParams = iframeParams.concat(
            window.location.href
              .substr(window.location.href.indexOf('?') + 1)
              .split('&')
          );
        }
        if (src && src.indexOf('?') > -1) {
          iframeParams = iframeParams.concat(
            src.substr(src.indexOf('?') + 1).split('&')
          );
          src = src.substr(0, src.indexOf('?'));
        }
        iframeParams.push('isIframeEmbed=1');
        ifr.src = src + '?' + iframeParams.join('&');
      }

      this.toggleEventListeners(true);
    },

    toggleEventListeners(add = false) {
      let method;
      if (window.addEventListener) {
        method = (add ? 'add' : 'remove') + 'EventListener';
        window[method]('message', this.handleIFrameMessage, false);
      } else if (window.attachEvent) {
        method = (add ? 'attach' : 'detach') + 'Event';
        window[method]('onmessage', this.handleIFrameMessage);
      }
    },

    handleIFrameMessage(e) {
      if (typeof e.data === 'object') {
        if (e.data?.action) {
          const { action, formID } = e.data;
          if (action === 'submission-completed' && formID === this.formId) {
            this.complete(0);
            this.$emit('completed');
          }
        }

        return;
      }

      var args = e.data.split(':');
      let iframe;
      if (args.length > 2) {
        iframe = document.getElementById(
          'JotFormIFrame-' + args[args.length - 1]
        );
      } else {
        iframe = document.getElementById('JotFormIFrame');
      }
      if (!iframe) {
        return;
      }

      switch (args[0]) {
        case 'scrollIntoView':
          iframe.scrollIntoView();
          break;
        case 'setHeight':
          iframe.style.height = +args[1] + (this.isMobile ? 50 : 0) + 'px';
          break;
        case 'collapseErrorPage':
          if (iframe.clientHeight > window.innerHeight) {
            iframe.style.height = window.innerHeight + 'px';
          }
          break;
        case 'reloadPage':
          window.location.reload();
          break;
        case 'loadScript':
          if (
            !this.isPermitted(e.origin, [
              'jotformWidget.com',
              'jotformWidget.pro',
            ])
          ) {
            break;
          }
          var src = args[1];
          if (args.length > 3) {
            src = args[1] + ':' + args[2];
          }
          var script = document.createElement('script');
          script.src = src;
          script.type = 'text/javascript';
          document.body.appendChild(script);
          break;
        case 'exitFullscreen':
          if (window.document.exitFullscreen) window.document.exitFullscreen();
          else if (window.document.mozCancelFullScreen)
            window.document.mozCancelFullScreen();
          else if (window.document.mozCancelFullscreen)
            window.document.mozCancelFullScreen();
          else if (window.document.webkitExitFullscreen)
            window.document.webkitExitFullscreen();
          else if (window.document.msExitFullscreen)
            window.document.msExitFullscreen();
          break;
      }

      var isJotForm = e.origin.indexOf('jotformWidget') > -1 ? true : false;
      if (
        isJotForm &&
        'contentWindow' in iframe &&
        'postMessage' in iframe.contentWindow
      ) {
        var urls = {
          docurl: encodeURIComponent(document.URL),
          referrer: encodeURIComponent(document.referrer),
        };
        iframe.contentWindow.postMessage(
          JSON.stringify({ type: 'urls', value: urls }),
          '*'
        );
      }
    },

    isPermitted(originUrl, whitelisted_domains) {
      var url = document.createElement('a');
      url.href = originUrl;
      var hostname = url.hostname;
      var result = false;
      if (typeof hostname !== 'undefined') {
        whitelisted_domains.forEach(function (element) {
          if (
            hostname.slice(-1 * element.length - 1) === '.'.concat(element) ||
            hostname === element
          ) {
            result = true;
          }
        });
        return result;
      }
    },
  },
  beforeDestroy() {
    this.toggleEventListeners();
  },
};
</script>

<style lang="less">
.jotform-widget {
  position: relative;

  &.widget-preview {
    &:after {
      height: 66px;
    }
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    bottom: 0;
    left: 0;
    height: 50px;
    width: 100%;
    background: var(--v-background-base);
    z-index: 1;
  }
}
</style>
